import { useResizeObserver } from '../../utilities/use-resize-observer';
import {
  DivergingBarChart,
  type DivergingBarChartProps,
} from './diverging-bar-chart';
import styles from './diverging-bar-with-arrows.module.css';
import { HorizontalArrows } from './horizontal-arrows';
import { VerticalArrows } from './vertical-arrows';

const ARROW_FOOTER_HEIGHT = 25;

export type DivergingBarWithArrowsProps = Omit<
  DivergingBarChartProps,
  'footer' | 'footerHeight'
> & {
  horizontalArrows?: {
    leftLabel?: string;
    rightLabel?: string;
  };
  verticalArrows?: {
    topLabel?: string;
    bottomLabel?: string;
    color?: string;
    fontFamily?: string;
  };
};

/**
 * `DivergingBarChart` + directional arrow overlays, composed here so callers
 * configure the overlays via plain data (`horizontalArrows`, `verticalArrows`)
 * instead of a render-prop.
 *
 * Why a composite rather than arrow props on `DivergingBarChart` itself:
 * the horizontal arrow needs the plot's `centerX` (which shifts with the
 * y-axis label column) to line up with the zero line. That's naturally a
 * `footer={(layout) => ...}` render-prop on the base chart — but shared
 * reports serialize each captured chart's props with `JSON.stringify`, and
 * function-valued props are silently dropped. A render-prop passed from the
 * app would render correctly in the live dashboard and then vanish in the
 * shared viewer.
 *
 * Keeping the render-prop usage *internal* to this composite means only
 * plain data crosses the share-capture boundary, so the viewer reconstructs
 * the same visual by re-rendering this component with the captured values.
 * `DivergingBarChart` stays slim and reusable on its own.
 */
export const DivergingBarWithArrows = ({
  horizontalArrows,
  verticalArrows,
  ...chartProps
}: DivergingBarWithArrowsProps) => {
  const { containerRef, width, height } = useResizeObserver();

  const showHorizontalArrows = Boolean(
    horizontalArrows?.leftLabel || horizontalArrows?.rightLabel
  );
  const showVerticalArrows = Boolean(
    verticalArrows?.topLabel || verticalArrows?.bottomLabel
  );

  const rowHeight = height - (showHorizontalArrows ? ARROW_FOOTER_HEIGHT : 0);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.row}>
        <div className={styles.plotArea}>
          <DivergingBarChart
            {...chartProps}
            footer={
              showHorizontalArrows
                ? (layout) => (
                    <HorizontalArrows
                      width={width}
                      centerX={layout.centerX}
                      leftLabel={horizontalArrows?.leftLabel}
                      rightLabel={horizontalArrows?.rightLabel}
                    />
                  )
                : undefined
            }
            footerHeight={
              showHorizontalArrows ? ARROW_FOOTER_HEIGHT : undefined
            }
          />
        </div>
        {showVerticalArrows && rowHeight > 0 && (
          <div className={styles.verticalArrowColumn}>
            <VerticalArrows
              height={rowHeight}
              topLabel={verticalArrows?.topLabel}
              bottomLabel={verticalArrows?.bottomLabel}
              color={verticalArrows?.color}
              fontFamily={verticalArrows?.fontFamily}
            />
          </div>
        )}
      </div>
    </div>
  );
};
